import { FC, useState } from 'react';
import { PageWrapper } from '@/components/ui';
import { useInternalDashboard } from '@/features/dashboard/hooks/useInternalDashboard';
import {
    InternalDashboardHeader,
    InternalDashboardSkeleton,
    InternalDashboardError,
    InternalPaymentSection,
    InternalUserSection,
    InternalSalesSection,
    InternalEngagementSection,
    InternalAutomationSection,
    InternalInfrastructureSection,
} from '@/features/dashboard/components/internal';
import { InternalTabKey } from '@/features/dashboard/types/internal';


const InternalDashboard: FC = () => {
    const {
        loading,
        data,
        error,
        dateRange,
        setDateRange,
        refresh
    } = useInternalDashboard();

    const [activeTab, setActiveTab] = useState<InternalTabKey>('overview');

    if (loading && !data) return <InternalDashboardSkeleton />;
    if (error && !data) return <InternalDashboardError error={error} onRetry={refresh} />;
    if (!data) return null; // Satisfies TS that data is defined

    // Contextual labels for the UI
    const periodLabel = dateRange === '7d' ? '7 Days' : dateRange === '30d' ? '30 Days' : '90 Days';
    const periodLabelFull = dateRange === '7d' ? 'last 7 days' : dateRange === '30d' ? 'last 30 days' : 'last 90 days';

    return (
        <PageWrapper
            title="Analytics"
            hideHeader
            containerClassName="p-0 bg-neutral-50 min-h-screen pb-12"
        >
            <InternalDashboardHeader
                activeTab={activeTab}
                onTabChange={setActiveTab}
                dateRange={dateRange}
                onDateRangeChange={setDateRange}
                onRefresh={refresh}
                isLoading={loading}
            />

            <div className="p-6">
                <div className="max-w-[1800px] mx-auto space-y-8">

                    {/* TABS CONTENT */}
                    {activeTab === 'overview' && (
                        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-500">
                            {/* FINANCE & GROWTH */}
                            <InternalPaymentSection
                                revenue={data.revenue}
                                periodLabel={periodLabel}
                            />

                            {/* USER PERFORMANCE */}
                            <InternalUserSection
                                users={data.users}
                                subscriptions={data.subscriptions}
                                periodLabel={periodLabel}
                            />

                            {/* SALES PIPELINE */}
                            <InternalSalesSection
                                leads={data.leads}
                                bookings={data.bookings}
                                emails={data.emails}
                                periodLabel={periodLabel}
                                periodLabelFull={periodLabelFull}
                            />

                            {/* ENGAGEMENT & MARKETING */}
                            <InternalEngagementSection
                                emails={data.emails}
                                posts={data.posts}
                                periodLabel={periodLabel}
                            />
                        </div>
                    )}

                    {activeTab === 'automation' && (
                        <div className="animate-in fade-in slide-in-from-bottom-2 duration-500">
                            <InternalAutomationSection
                                automation={data.automation}
                                periodLabel={periodLabel}
                                periodLabelFull={periodLabelFull}
                            />
                        </div>
                    )}

                    {activeTab === 'infrastructure' && (
                        <div className="animate-in fade-in slide-in-from-bottom-2 duration-500">
                            <InternalInfrastructureSection
                                services={data.services}
                                loading={loading}
                                refresh={refresh}
                                error={error}
                            />
                        </div>
                    )}


                </div>
            </div>
        </PageWrapper>
    );
};

export default InternalDashboard;
